/**
 * The id-trail recovery over the headings of one scan: the `parent_id_path=` for a heading whose
 * `parent=` path does not walk, and the `owner_uid_path=` for one whose `owner=` path does not.
 * The tree builder, the placement facts and the connection-end resolver ask it here.
 */
import { INSTANCE_PLACEHOLDER_TYPE, recoverByIdTrail, } from '../godot/scene/packedScene.js';
import { nodePathNames, simplifiedNames, simplifiedNodePath } from '../godot/string/nodePath.js';
import { firstIndexWith } from '../utils/keyedLookup.js';
import { joinPath } from '../utils/nodePath.js';
/**
 * The path segment that stands for the node an id trail finds inside an instance, which only that
 * instance's graft can name. No heading writes it, since `:` ends a name in a NodePath.
 *
 * @canonical The path segment of a node a content trail finds
 */
export function contentTrailSegment(trail) {
    return CONTENT_TRAIL_MARK + trail.join(',');
}
const CONTENT_TRAIL_MARK = ':';
/** Whether `path` passes through the segment of a content trail ({@link contentTrailSegment}). */
export function passesContentTrail(path) {
    return path.includes(CONTENT_TRAIL_MARK);
}
/**
 * `path` simplified as `NodePath::simplify` simplifies its names. A live path holds names only, so
 * the segment of a content trail stays a name where a NodePath would read `:` as its subnames.
 */
export function simplifiedLivePath(path) {
    return simplifiedNames(nodePathNames(path)).join('/');
}
/** The first heading of a scan carrying an id, the one index the builder and the linter share. */
const firstOriginWithId = firstIndexWith((origin) => origin.uniqueId);
/** Where the trail of `origins[index]` seats it, or undefined where it resolves nowhere or has no trail. */
export function idTrailEnd(origins, index, isPlaced) {
    const trail = origins[index]?.idTrail;
    if (trail === undefined)
        return undefined;
    return trailEnd(trail, origins, isPlaced, index);
}
/**
 * Where the `owner_uid_path=` of `origins[index]` finds its owner, or undefined where it finds
 * nothing or there is none. Instantiate sets the owner once the node itself is built
 * (`packed_scene.cpp:565-568`), so the heading counts among the built ones.
 */
export function ownerTrailEnd(origins, index, isPlaced) {
    const trail = origins[index]?.ownerIdTrail;
    if (trail === undefined)
        return undefined;
    return trailEnd(trail, origins, isPlaced, index + 1);
}
/**
 * Where `trail` lands once every heading is built, as a `[connection]` end looks it up: instantiate
 * makes the connections after the build (`packed_scene.cpp:647-660`).
 */
export function builtSceneTrailEnd(trail, origins, isPlaced) {
    return trailEnd(trail, origins, isPlaced, origins.length);
}
/**
 * Where `trail` lands over the first `builtCount` headings, as instantiate has built them. Only the
 * heading the trail names is read, so the cost does not grow with the other headings.
 */
function trailEnd(trail, origins, isPlaced, builtCount) {
    return recoverByIdTrail(trail, (id) => firstOriginWithId(origins, id), (index) => headingOf(origins[index], isPlaced), builtCount);
}
function headingOf(origin, isPlaced) {
    return { uniqueId: origin.uniqueId, instance: instanceKind(origin.node), isPlaced: isPlaced(origin) };
}
/**
 * What a trail that enters the scene reads of each heading, in file order: the headings a seating
 * leaves out of its tree too, since `ids.find` reads them all (`packed_scene.cpp:1960`).
 */
export function trailHeadingsOf(origins) {
    return origins.map(({ node, declaredParent, uniqueId }) => ({
        uniqueId,
        path: declaredParent === undefined ? '' : authoredPath(declaredParent, node.name),
        instance: instanceKind(node),
        instanceRef: node.instance,
    }));
}
/** The `parent=` a heading writes for a child of the root. */
const ROOT_PARENT = '.';
/** The simplified path of a heading below `parent`. A child of the root, most headings, needs no simplifying. */
function authoredPath(parent, name) {
    return parent === ROOT_PARENT ? name : simplifiedNodePath(joinPath(parent, name));
}
function instanceKind(node) {
    if (node.type === INSTANCE_PLACEHOLDER_TYPE)
        return 'placeholder';
    return node.instance === undefined ? undefined : 'scene';
}
//# sourceMappingURL=idTrail.js.map